import type {
  DashboardUser,
  Meeting,
  ScheduleMeetingPayload,
  UpdateScheduleMeetingPayload,
  PersonalMeetingId,
  PersonalMeetingIdPayload,
} from '@/lib/types/dashboard';

const API_URL = process.env.NEXT_PUBLIC_API_URL 
  ? `${process.env.NEXT_PUBLIC_API_URL}`
  : 'http://localhost:8000/';

type AnyRecord = Record<string, any>;

async function parseResponse<T>(res: Response, fallbackMessage: string): Promise<T> {
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const errors = data?.errors ? Object.values(data.errors).flat().join(' ') : '';
    throw new Error(errors || data?.message || data?.error || fallbackMessage);
  }

  return data;
}


function normalizeMeeting(raw: AnyRecord): Meeting {
  return {
    id: raw.id,
    roomName: raw.room_name || raw.roomName || '',
    title: raw.title || '',
    description: raw.description,
    permission: raw.permission || 'public',
    scheduled_at: raw.scheduled_at || raw.start_time || new Date().toISOString(),
    duration_minutes: Number(raw.duration_minutes ?? raw.duration ?? 0),
    max_participants: raw.max_participants,
    enable_recording: raw.enable_recording,
    auto_start_recording: raw.auto_start_recording,
    allow_microphone: raw.allow_microphone,
    allow_camera: raw.allow_camera,
    allow_screen_share: raw.allow_screen_share,
    allow_chat: raw.allow_chat,
    empty_timeout: raw.empty_timeout,
    departure_timeout: raw.departure_timeout,
    room_password: raw.room_password,
    creator: raw.creator,
    invited_users: raw.invited_users,
    join_url: raw.join_url,
  };
}

function unwrapOne(data: any): AnyRecord {
  if (data?.data && typeof data.data === 'object' && !Array.isArray(data.data)) return data.data;
  if (data?.meeting && typeof data.meeting === 'object') return data.meeting;
  if (data?.scheduled_meeting && typeof data.scheduled_meeting === 'object') return data.scheduled_meeting;
  return data;
}

function unwrapMany(data: any): AnyRecord[] {
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.meetings)) return data.meetings;
  if (Array.isArray(data?.scheduled_meetings)) return data.scheduled_meetings;
  if (Array.isArray(data)) return data;
  return [];
}

export async function getUpcomingMeetings(token: string): Promise<Meeting[]> {
  const res = await fetch(`${API_URL}/scheduled-meetings`, {
    headers: { 
      'Accept': 'application/json',
      Authorization: `Bearer ${token}` 
    },
  });

  const data = await parseResponse<any>(res, 'Failed to fetch upcoming meetings');
  return unwrapMany(data).map(normalizeMeeting);
}

export type MeetingHistoryItem = {
  meeting_id: number;
  room_name: string | null;
  room_code: string | null;
  title: string | null;
  role: string | null;
  joined_at: string | null;
  left_at: string | null;
  duration_seconds: number | null;
  is_active: boolean;
  has_recording: boolean;
};

export async function getMeetingHistory(token: string): Promise<MeetingHistoryItem[]> {
  const res = await fetch(`${API_URL}/auth/meeting-history`, {
    headers: {
      'Accept': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    cache: 'no-store',
  });

  const data = await parseResponse<{ history?: MeetingHistoryItem[] }>(res, 'Gagal memuat riwayat meeting');
  return data.history ?? [];
}

export async function getScheduledMeetingDetail(token: string, roomName: string): Promise<Meeting> {
  const res = await fetch(`${API_URL}/scheduled-meetings/${roomName}`, {
    headers: { 
      'Accept': 'application/json',
      Authorization: `Bearer ${token}` 
    },
  });

  const data = await parseResponse<any>(res, 'Failed to fetch meeting detail');
  return normalizeMeeting(unwrapOne(data));
}

export async function scheduleMeeting(token: string, payload: ScheduleMeetingPayload): Promise<Meeting> {
  const res = await fetch(`${API_URL}/scheduled-meetings`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const data = await parseResponse<any>(res, 'Failed to schedule meeting');
  return normalizeMeeting(unwrapOne(data));
}

export async function updateScheduledMeeting(
  token: string,
  id: string | number,
  payload: UpdateScheduleMeetingPayload
): Promise<Meeting> {
  // FIX: Backend route edit schedule adalah PATCH /api/scheduled-meetings/id/{id}; hindari URL .../apiapi yang memicu Failed to fetch
  const apiBase = API_URL.replace(/\/$/, '').replace(/\/api$/, '');
  const res = await fetch(`${apiBase}/api/scheduled-meetings/id/${id}`, {
    method: 'PATCH',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseResponse<any>(res, 'Failed to update scheduled meeting');
  return normalizeMeeting(unwrapOne(data));
}

export async function deleteScheduledMeeting(token: string, scheduleId: string | number): Promise<void> {
  const res = await fetch(`${API_URL}/scheduled-meetings/id/${scheduleId}`, {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  await parseResponse(res, 'Failed to delete scheduled meeting');
}

export async function joinScheduledMeeting(token: string, roomName: string, password?: string): Promise<any> {
  const payload = password ? { password } : {};
  const res = await fetch(`${API_URL}/scheduled-meetings/${roomName}/join`, {
    method: 'POST',
    headers: { 
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(payload),
  });

  return parseResponse<any>(res, 'Failed to join meeting');
}

export async function getPersonalMeetingId(token: string): Promise<PersonalMeetingId | null> {
  const pmiBaseUrl = API_URL.endsWith('/api') ? `${API_URL}/personal-meeting-id` : `${API_URL.replace(/\/$/, '')}/api/personal-meeting-id`;
  try {
    const res = await fetch(pmiBaseUrl, {
      headers: { 
        'Accept': 'application/json',
        Authorization: `Bearer ${token}` 
      },
    });
    if (res.status === 404) return null; // assume not found means no pmi
    const data = await parseResponse<any>(res, 'Failed to fetch PMI');
    const pmi = data?.personal_meeting_id;
    if (!pmi) return null;
    return {
      ...pmi,
      roomName: pmi.room_name || pmi.roomName,
    };
  } catch (e) {
    return null;
  }
}

export async function savePersonalMeetingId(token: string, payload: PersonalMeetingIdPayload): Promise<PersonalMeetingId> {
  const pmiBaseUrl = API_URL.endsWith('/api') ? `${API_URL}/personal-meeting-id` : `${API_URL.replace(/\/$/, '')}/api/personal-meeting-id`;
  const res = await fetch(pmiBaseUrl, {
    method: 'POST', // The docs specify POST to create
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  
  const data = await parseResponse<any>(res, 'Failed to save PMI');
  const pmi = data?.personal_meeting_id;
  return {
    ...pmi,
    roomName: pmi.room_name || pmi.roomName,
  };
}

export async function joinPersonalMeetingId(token: string, roomName: string, password?: string): Promise<any> {
  const pmiBaseUrl = API_URL.endsWith('/api') ? `${API_URL}/personal-meeting-id` : `${API_URL.replace(/\/$/, '')}/api/personal-meeting-id`;
  const payload = password ? { password } : {};
  const res = await fetch(`${pmiBaseUrl}/${roomName}/join`, {
    method: 'POST',
    headers: { 
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(payload),
  });

  return parseResponse<any>(res, 'Failed to join PMI');
}

export async function claimHostPersonalMeetingId(token: string, roomName: string, hostPassword?: string): Promise<any> {
  const pmiBaseUrl = API_URL.endsWith('/api') ? `${API_URL}/personal-meeting-id` : `${API_URL.replace(/\/$/, '')}/api/personal-meeting-id`;
  const payload = hostPassword ? { host_password: hostPassword } : {};
  const res = await fetch(`${pmiBaseUrl}/${roomName}/claim-host`, {
    method: 'POST',
    headers: { 
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(payload),
  });

  return parseResponse<any>(res, 'Failed to claim host');
}

export async function claimHostScheduledMeeting(token: string, roomName: string, hostPassword?: string): Promise<any> {
  const res = await fetch(`${API_URL}/scheduled-meetings/${roomName}/claim-host`, {
    method: 'POST',
    headers: { 
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}` 
    },
    body: JSON.stringify(hostPassword ? { host_password: hostPassword } : {}),
  });

  return parseResponse<any>(res, 'Failed to claim host');
}

export async function searchUsers(token: string, query: string): Promise<{ id: number; name: string; email: string }[]> {
  const res = await fetch(`${API_URL}/users/search?q=${encodeURIComponent(query)}`, {
    headers: {
      'Accept': 'application/json',
      Authorization: `Bearer ${token}`
    }
  });
  
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return data?.users || [];
}

export async function updateStatus(status: DashboardUser['status']): Promise<void> {
  void status;
}
